import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import type {
  OrgUnitDecision,
  OrgValidationNode,
  OrgValidationUpdate,
} from "@/api/diagnostic-org-structure";
import { formatDateTime } from "@/lib/datetime";
import { cn } from "@/lib/utils";

import { DECISION_STYLE, type DecisionView } from "./decisionLabels";

/** O que uma linha pode mandar gravar: só os campos que mudaram. */
export type ValidationPatch = Pick<OrgValidationUpdate, "data_matches" | "decision" | "note">;

/**
 * Quem pode editar e por onde a linha grava.
 *
 * Os controles não travam enquanto um PUT está em voo: travar tiraria o foco da
 * observação logo depois do "Não", que é justamente para onde ele vai.
 *
 * `onSave` termina quando a gravação acabou, com sucesso ou não, e a árvore já
 * foi relida: só então a célula deixa de mostrar a escolha e volta ao servidor.
 */
export interface ValidationEditing {
  canEdit: boolean;
  onSave(node: OrgValidationNode, patch: ValidationPatch): Promise<void>;
}

const DECISION_ORDER: readonly DecisionView[] = [
  "pending",
  "in_scope",
  "client_validation",
  "out_of_scope",
];

/**
 * As três células da validação com o cliente: Confere?, Escopo e Observação.
 *
 * A unidade fora do escopo pelo nível acima fica travada inteira, porque o PUT
 * a recusa (409) em qualquer campo. "Não" em Confere leva o foco à observação,
 * que é onde a divergência se explica.
 */
export function ValidationCells({
  node,
  title,
  decisions,
  editing,
}: {
  node: OrgValidationNode;
  title: string;
  decisions: Record<DecisionView, string>;
  editing: ValidationEditing;
}) {
  const { t } = useTranslation();
  const noteRef = useRef<HTMLInputElement>(null);
  const saved = node.validation.note ?? "";
  const [note, setNote] = useState(saved);
  const [focused, setFocused] = useState(false);
  // A releitura depois de gravar traz a observação do servidor, e ela substitui
  // o rascunho, mas só com o campo sem foco: com foco, a pessoa voltou a digitar,
  // e o que ela digitou depois da última gravação vale até sair do campo. Ajuste
  // durante a renderização, e não num efeito, como o React recomenda para estado
  // que segue uma prop.
  const [base, setBase] = useState(saved);
  if (base !== saved) {
    setBase(saved);
    if (!focused) setNote(saved);
  }

  // A escolha que ainda está gravando. Sem ela, o select e o Sim/Não voltariam ao
  // valor antigo até a resposta e a releitura, e a pessoa escolheria de novo.
  const [chosenDecision, setChosenDecision] = useState<DecisionView | undefined>(undefined);
  const [chosenMatches, setChosenMatches] = useState<boolean | null | undefined>(undefined);

  const inherited = node.scope_inherited;
  const locked = !editing.canEdit || inherited;
  const matches =
    chosenMatches !== undefined ? chosenMatches : (node.validation.data_matches ?? null);
  const decision: DecisionView = inherited
    ? "out_of_scope"
    : (chosenDecision ?? node.validation.decision ?? "pending");

  function answer(value: boolean) {
    const next = matches === value ? null : value;
    setChosenMatches(next);
    void editing.onSave(node, { data_matches: next }).finally(() => setChosenMatches(undefined));
    if (!value) noteRef.current?.focus();
  }

  function choose(value: string) {
    const next = (value || null) as OrgUnitDecision | null;
    setChosenDecision(next ?? "pending");
    void editing.onSave(node, { decision: next }).finally(() => setChosenDecision(undefined));
  }

  function saveNote() {
    setFocused(false);
    const next = note.trim();
    if (next === saved) return;
    void editing.onSave(node, { note: next || null });
  }

  return (
    <>
      <td className="px-3 py-2.5">
        <div className="inline-flex overflow-hidden rounded-md border border-[#d4dae3]">
          <AnswerButton
            label={t("Yes")}
            pressed={matches === true}
            tone="yes"
            disabled={locked}
            onClick={() => answer(true)}
          />
          <AnswerButton
            label={t("No")}
            pressed={matches === false}
            tone="no"
            disabled={locked}
            onClick={() => answer(false)}
          />
        </div>
      </td>
      <td className="px-3 py-2.5">
        <select
          aria-label={t("Scope of {{name}}", { name: title })}
          value={decision === "pending" ? "" : decision}
          disabled={locked}
          onChange={(event) => choose(event.target.value)}
          className={cn(
            "rounded-md border px-2 py-1 text-[12px] font-medium disabled:cursor-not-allowed disabled:opacity-70",
            DECISION_STYLE[decision],
          )}
        >
          {DECISION_ORDER.map((option) => (
            <option key={option} value={option === "pending" ? "" : option}>
              {decisions[option]}
            </option>
          ))}
        </select>
      </td>
      <td className="px-3 py-2.5">
        <input
          ref={noteRef}
          type="text"
          aria-label={t("Note for {{name}}", { name: title })}
          value={note}
          disabled={locked}
          maxLength={2000}
          placeholder={
            inherited ? t("Out of scope because of the level above") : t("Justify the decision")
          }
          onChange={(event) => setNote(event.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={saveNote}
          className="w-full min-w-[160px] rounded-md border border-[#d4dae3] px-2 py-1 text-[12px] text-[#08254c] placeholder:text-[#9aa5b5] disabled:cursor-not-allowed disabled:bg-[#f5f8fc]"
        />
        <DecidedBy node={node} />
      </td>
    </>
  );
}

function AnswerButton({
  label,
  pressed,
  tone,
  disabled,
  onClick,
}: {
  label: string;
  pressed: boolean;
  tone: "yes" | "no";
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={pressed}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "px-2.5 py-1 text-[12px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#08254c] disabled:cursor-not-allowed disabled:opacity-60",
        pressed
          ? tone === "yes"
            ? "bg-[#e7f6ec] text-[#1e7b43]"
            : "bg-[#fde8e6] text-[#a23b2f]"
          : "bg-white text-[#596b85] hover:bg-[#f5f8fc]",
      )}
    >
      {label}
    </button>
  );
}

/**
 * "Decidido por Fulana em 15/10/2026 14:32", ou só a data quando o nome não veio.
 *
 * Só com decisão de escopo: a unidade Pendente não tem quem a decidiu, mesmo
 * que tenha observação ou tenha voltado a Pendente pelo nível acima.
 */
function DecidedBy({ node }: { node: OrgValidationNode }) {
  const { t } = useTranslation();
  const { decided_at: decidedAt, decided_by_name: name, decision } = node.validation;
  if (!decidedAt || !decision) return null;

  const date = formatDateTime(decidedAt);
  return (
    <div className="mt-1 text-[10px] text-[#7b8799]">
      {name ? t("Decided by {{name}} on {{date}}", { name, date }) : t("Decided on {{date}}", { date })}
    </div>
  );
}
